import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  createCommand,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  LexicalCommand,
  LexicalEditor,
  TextNode
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { $createMentionNode, MentionPayload } from '../nodes/mention.node';

export interface MentionMenuOpen {
  query: string;
  anchorRect: DOMRect;
}

export type MentionMenuState = MentionMenuOpen | 'closed';

export const INSERT_MENTION_COMMAND: LexicalCommand<MentionPayload> =
  createCommand('INSERT_MENTION_COMMAND');

export interface MentionPlugin {
  state$: Observable<MentionMenuState>;
  unregister: () => void;
}

export function registerMentionPlugin(editor: LexicalEditor): MentionPlugin {
  const state$ = new BehaviorSubject<MentionMenuState>('closed');

  let mentionStartOffset: number | null = null;
  let mentionNodeKey: string | null = null;

  const close = () => {
    state$.next('closed');
    mentionStartOffset = null;
    mentionNodeKey = null;
  };

  const isOpen = () => state$.value !== 'closed';

  const unregister = mergeRegister(
    // Track "@" mentions as user types
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const mention = findMentionAtCursor();
        if (mention) {
          mentionStartOffset = mention.startOffset;
          mentionNodeKey = mention.nodeKey;
          state$.next({ query: mention.query, anchorRect: mention.anchorRect });
        } else if (isOpen()) {
          close();
        }
      });
    }),

    // Insert mention when user selects from dropdown
    editor.registerCommand(
      INSERT_MENTION_COMMAND,
      payload => {
        if (mentionStartOffset === null || mentionNodeKey === null)
          return false;

        const nodeKey = mentionNodeKey;
        const startOffset = mentionStartOffset;

        editor.update(() => {
          const textNode = $getNodeByKey(nodeKey);
          if (!$isTextNode(textNode)) return;

          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          insertMention(
            textNode,
            startOffset,
            selection.anchor.offset,
            payload
          );
          close();
        });

        return true;
      },
      COMMAND_PRIORITY_LOW
    ),

    // Close on Escape
    editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        if (isOpen()) {
          close();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    ),

    // Block Enter/Tab when menu is open (handled by dropdown component)
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      isOpen,
      COMMAND_PRIORITY_CRITICAL
    ),
    editor.registerCommand(KEY_TAB_COMMAND, isOpen, COMMAND_PRIORITY_CRITICAL)
  );

  return { state$: state$.asObservable(), unregister };
}

/**
 * Finds an active "@mention" at the current cursor position.
 * Returns null if cursor is not in a valid mention context.
 */
function findMentionAtCursor(): {
  query: string;
  anchorRect: DOMRect;
  startOffset: number;
  nodeKey: string;
} | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;

  const anchorNode = selection.anchor.getNode();
  if (!$isTextNode(anchorNode)) return null;

  const textBeforeCursor = anchorNode
    .getTextContent()
    .slice(0, selection.anchor.offset);
  const atIndex = textBeforeCursor.lastIndexOf('@');

  if (atIndex === -1) return null;

  // "@" must be at start of text or preceded by whitespace
  const charBefore = textBeforeCursor[atIndex - 1];
  if (atIndex > 0 && charBefore !== ' ' && charBefore !== '\n') return null;

  // Query cannot contain spaces or newlines
  const query = textBeforeCursor.slice(atIndex + 1);
  if (query.includes(' ') || query.includes('\n')) return null;

  const anchorRect = window
    .getSelection()
    ?.getRangeAt(0)
    ?.getBoundingClientRect();
  if (!anchorRect) return null;

  return {
    query,
    anchorRect,
    startOffset: atIndex,
    nodeKey: anchorNode.getKey()
  };
}

/**
 * Replaces "@query" text with a MentionNode and adds a trailing space.
 */
function insertMention(
  textNode: TextNode,
  startOffset: number,
  endOffset: number,
  payload: MentionPayload
): void {
  // Split: [before @] [@ + query] [after cursor]
  const splitNodes = textNode.splitText(startOffset, endOffset);
  const mentionText = startOffset === 0 ? splitNodes[0] : splitNodes[1];

  // Replace "@query" with mention node + space
  const mentionNode = $createMentionNode(payload);
  mentionText.replace(mentionNode);
  const spaceNode = new TextNode(' ');
  mentionNode.insertAfter(spaceNode);
  spaceNode.select(1, 1);
}
